import _ from "lodash";
import { getAssessmentDetailsFromCache } from "Assessments/modules/AssessmentGraphqlHelpers";
import { getUnitPlanFieldsFromAssessment } from "Assessments/modules/Utils";
import { PERCENTAGE_UPPERCASE } from "Constants/stringConstants";

const getCourseIdsMemoize = _.memoize(
  params => getCourseIds(params),
  params => {
    const { associatedEntities = [], entityType } = params;

    const entityKeys = _.chain(associatedEntities)
      .map(entity => `${entity.__typename}:${entity.id}`)
      .sort()
      .join(",")
      .value();

    return `${entityType}|${entityKeys}`;
  }
);

const getCourseIds = ({ associatedEntities, entityType }) => {
  return _.map(
    _.filter(associatedEntities, c => _.get(c, "__typename") === entityType),
    c => _.get(c, "id")
  );
};

const getDropdownItemsMemoize = _.memoize(
  params => getDropdownItems(params),
  params => {
    const {
      categories = [],
      subjectLevels = [],
      isTOK = false,
      isAbsoluteWeightageType = false,
      isCategoryWeightageEnabled,
      isNewAssessmentSettingsEnabled,
      categoryTotalDisplayType,
    } = params;

    // For categories, we only need id, label, and specific value properties
    const categoryKeys = _.map(categories, category => {
      const values = _.map(category.values, value => {
        if (!value) return "";

        return _.chain([
          value.genericTag?.id,
          value.genericTag?.label,
          isNewAssessmentSettingsEnabled &&
          categoryTotalDisplayType === "PERCENTAGE"
            ? value.percentageValue
            : value.value,
        ])
          .compact()
          .join(":")
          .value();
      })
        .sort()
        .join(",");

      return `${category.id}|${category.label}|${values}`;
    }).join(";;");

    return _.chain([
      categoryKeys,
      _.chain(subjectLevels).sortBy().join(",").value(),
      isTOK,
      isAbsoluteWeightageType,
      isCategoryWeightageEnabled,
      isNewAssessmentSettingsEnabled,
      categoryTotalDisplayType,
    ])
      .compact()
      .join("|")
      .value();
  }
);

const getDropdownItems = ({
  categories,
  subjectLevels = [],
  isTOK = false,
  isAbsoluteWeightageType = false,
  t,
  isCategoryWeightageEnabled,
  isNewAssessmentSettingsEnabled,
  categoryTotalDisplayType,
}) => {
  const isCategoryDisplayTypePercentage =
    categoryTotalDisplayType === PERCENTAGE_UPPERCASE;

  const showConditionalPercentage = isNewAssessmentSettingsEnabled
    ? isCategoryDisplayTypePercentage
    : !isAbsoluteWeightageType;
  const conditionalPercentage = showConditionalPercentage ? "%" : "";

  const dropdownItems = _.map(categories, category => {
    let categoryLabel = _.get(category, "label", "");
    let percentageSubLabel = "";
    const categoryValues = _.orderBy(
      _.get(category, "values", []),
      "genericTag.label",
      "desc"
    );

    if (isCategoryWeightageEnabled) {
      const values = [];

      _.forEach(categoryValues, categoryValue => {
        const value =
          isNewAssessmentSettingsEnabled && isCategoryDisplayTypePercentage
            ? _.get(categoryValue, "percentageValue")
            : _.get(categoryValue, "value");

        if (_.isNil(value)) {
          return;
        }

        const genericTag = _.get(categoryValue, "genericTag");
        const formattedValue = _.join(
          _.concat(value, conditionalPercentage),
          ""
        );

        if (genericTag) {
          const genericTagId = _.get(genericTag, "id");
          if (
            !_.isEmpty(subjectLevels) &&
            !_.includes(subjectLevels, genericTagId)
          ) {
            return;
          }
          const genericTagLabel = _.get(genericTag, "label");
          values.push(`${formattedValue} (${genericTagLabel})`);
        } else {
          values.push(`(${formattedValue})`);
        }
      });

      if (_.size(categoryValues) === 1 || isTOK) {
        if (!_.isEmpty(values)) {
          categoryLabel += ` ${_.first(values)}`;
        }
      } else {
        if (!_.isEmpty(values)) {
          percentageSubLabel = _.join(values, ", ");
        }
      }
    }

    return {
      label: categoryLabel,
      key: _.get(category, "id"),
      subtitle: percentageSubLabel,
    };
  });

  return [
    ...dropdownItems,
    {
      label: t("common:none"),
      key: -1,
    },
  ];
};

const getSubjectsFromResolvedMinimalTree = fields => {
  return _.get(
    _.find(fields, { uid: "subjects" }),
    "resolvedMinimalTree.subjects",
    []
  );
};

const getUnitPlanRelatedData = ({ assessmentId, collaboratorsFilter }) => {
  const assessmentDetails = getAssessmentDetailsFromCache({
    id: assessmentId,
    collaboratorsFilter,
  });
  const unitPlanFields = _.get(assessmentDetails, "unitPlan.allFields", []);

  let unitPlanSubjects = getSubjectsFromResolvedMinimalTree(unitPlanFields);

  if (_.isEmpty(unitPlanSubjects)) {
    const unitPlanFields = getUnitPlanFieldsFromAssessment({
      assessmentDetails,
    });
    unitPlanSubjects = getSubjectsFromResolvedMinimalTree(unitPlanFields);
  }

  return {
    unitPlanSubjects,
  };
};

/**
 *
 * @param {Array | String | Null} dependentFieldValue if subjects are radio button then Array, if dropdown then String
 * @param {Array} unitPlanSubjects
 * @returns {String | Null}
 */
/**
 * Test Cases
 * dependentFieldValue = null  -> return null
 * dependentFieldValue = ["7484","8937","7563"]  -> return null
 * dependentFieldValue = "6757"  -> return "6757"
 * dependentFieldValue = ["6757"]  -> return "6757"
 *
 */
const getSelectedSubjectId = ({ dependentFieldValue }) => {
  let dfv = dependentFieldValue;

  if (_.isNull(dfv)) {
    dfv = null;
  } else if (_.isArray(dfv)) {
    if (_.size(dfv) === 1) {
      dfv = _.first(dfv);
    } else {
      dfv = null;
    }
  }

  return {
    selectedSubjectId: dfv,
  };
};

const getSelectedSubjectIdMemoize = _.memoize(
  params => getSelectedSubjectId(params),
  ({ dependentFieldValue }) => {
    if (_.isArray(dependentFieldValue)) {
      return _.join(dependentFieldValue, ",");
    }
    return dependentFieldValue;
  }
);

/**
 * @deprecated not being used, not updated with academicYearId in filters
 * If anyone is about to use the following function, do update the following functions:
 * getUnitPlanCentralRepoInputAndFilters - pass academicYearId
 * getUnitPlanDetailsFromCache - pass collaborator filters with academicYearId
 */

const getAssociatedEntitiesVariables = ({
  isCourseApplicableToCategorySet,
  courseId,
  isAdminPage,
  adminUnitPlanGradeId,
  gradeId,
}) => {
  if (isCourseApplicableToCategorySet) {
    return {
      type: "COURSE",
      ids: courseId,
    };
  }
  return {
    type: "GRADE",
    ids: isAdminPage ? adminUnitPlanGradeId : gradeId,
  };
};

export {
  getUnitPlanRelatedData,
  getDropdownItemsMemoize,
  getCourseIdsMemoize,
  getSelectedSubjectIdMemoize,
  getAssociatedEntitiesVariables,
};
